import { Button, InputAdornment, Stack, TextField } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';

import useDialog from '../hooks/useDialog';
import useBackdrop from '../hooks/useBackdrop';
import useSnackbar from '../hooks/useSnackbar';
import { createWithdrawalRequest } from '../fetchFns';

interface CreateWithdrawalRequestDialogProps {
  /** Called after a request is created so the list can refresh. */
  onCreated: () => void;
}

type FormValues = {
  amount: string;
};

const defaultValues: FormValues = {
  amount: '',
};

export default function CreateWithdrawalRequestDialog({
  onCreated,
}: CreateWithdrawalRequestDialogProps) {
  const dialog = useDialog();
  const backdrop = useBackdrop();
  const snackbar = useSnackbar();
  const {
    handleSubmit,
    control,
    reset,
    formState: { isSubmitting },
  } = useForm<FormValues>({
    defaultValues,
    mode: 'onTouched',
  });

  const handleCreateWithdrawalRequest = async (data: FormValues) => {
    backdrop.handleBackdropOpen();
    try {
      await createWithdrawalRequest(Number(data.amount));
      reset();
      dialog.handleDialogClose();
      onCreated();
      snackbar.handleSnackbarOpen(
        'Your withdrawal request has been submitted.',
        'success',
      );
    } catch (error) {
      const errorCast = error as Error;
      console.error(errorCast.stack);
      snackbar.handleSnackbarOpen(errorCast.message, 'error');
    }
    backdrop.handleBackdropClose();
  };

  return (
    <Stack
      component="form"
      direction="column"
      spacing={2}
      onSubmit={handleSubmit(handleCreateWithdrawalRequest)}>
      <Controller
        name="amount"
        control={control}
        rules={{
          required: 'Please enter an amount.',
          validate: (value) => {
            const amount = Number(value);
            if (Number.isNaN(amount)) return 'Amount must be a number.';
            if (amount <= 0) return 'Amount must be greater than zero.';
            return true;
          },
        }}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            label="Amount to withdraw"
            type="number"
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
              },
            }}
            fullWidth
          />
        )}
      />
      <Stack justifyContent="end" direction="row" spacing={2}>
        <Button
          onClick={() => {
            dialog.handleDialogClose();
          }}>
          Cancel
        </Button>
        <Button type="submit" variant="contained" disabled={isSubmitting}>
          Submit Request
        </Button>
      </Stack>
    </Stack>
  );
}
